import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useState, useCallback, useMemo, memo, startTransition } from 'react'
import { ArrowUpDown, ChevronDown, PlusIcon, Trash2 } from 'lucide-react'
import { cn, type FilterOption } from '@/lib/utils'
import { formatSortFieldName } from '@/lib/formatters'
import type { SortingState } from '@tanstack/react-table'
import { FiltersCombobox } from '@/components/custom/filters-combobox'
import { SortRow } from '@/components/custom/sort-combobox/components/sort-row'
import { useAutoOpenPopover } from '@/hooks/use-auto-open-popover'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useIsMobile } from '@/hooks/use-mobile'

interface SortComboboxProps {
  sorting: SortingState
  filtersList?: FilterOption[]
  newlyAddedSort?: string | null
  onSortSelect?: (sortValue: string) => void
  onSortUpdate?: (oldSort: string, newSortValue: SortingState) => void
  onSortReorder?: (newSorting: SortingState) => void
  onClearNewlyAddedSort?: () => void
  clearSorts?: (sortId?: string) => void
  className?: string
}

function SortCombobox({
  sorting,
  filtersList = [],
  className,
  newlyAddedSort,
  onSortSelect,
  onSortUpdate,
  onSortReorder,
  onClearNewlyAddedSort,
  clearSorts,
}: SortComboboxProps) {
  const [open, setOpen] = useState(false)
  const isMobile = useIsMobile()

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  useAutoOpenPopover(!!newlyAddedSort, setOpen, onClearNewlyAddedSort)

  const cleanFiltersList = useMemo(
    () =>
      filtersList.filter(
        (filter) => !sorting.find((sort) => sort.id === filter.key)
      ),
    [filtersList, sorting]
  )

  const clearAllSorts = useCallback(() => {
    if (!clearSorts) return
    clearSorts()

    startTransition(() => {
      setOpen(false)
    })
  }, [clearSorts])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      if (!over || !onSortReorder) return

      if (active.id !== over.id) {
        const oldIndex = sorting.findIndex((sort) => sort.id === active.id)
        const newIndex = sorting.findIndex((sort) => sort.id === over.id)

        const newSorting = arrayMove(sorting, oldIndex, newIndex)
        onSortReorder(newSorting)
      }
    },
    [sorting, onSortReorder]
  )

  // Helper to get display label from filtersList or fall back to formatted field name
  const getSortLabel = useCallback(
    (sortId: string) => {
      const option = filtersList.find((f) => f.key === sortId)
      return option?.label ?? formatSortFieldName(sortId)
    },
    [filtersList]
  )

  const sortDisplayText = useMemo(() => {
    if (sorting.length === 1 && !isMobile) {
      return `${getSortLabel(sorting[0].id)}: ${sorting[0].desc ? 'Descending' : 'Ascending'}`
    }
    return `${sorting.length} Sort${sorting.length > 1 ? 's' : ''}`
  }, [sorting, isMobile, getSortLabel])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-label={sortDisplayText}
          aria-expanded={open}
          className={cn(
            'gap-2 border-primary/20 bg-transparent text-primary shadow-none [&>svg]:text-primary hover:bg-primary/5',
            'data-[state=open]:border-primary/50 data-[state=open]:bg-primary/10 data-[state=open]:text-primary data-[state=open]:[&>svg]:text-primary',
            className
          )}
        >
          <ArrowUpDown className="size-3.5" />
          {sortDisplayText}
          <ChevronDown className="size-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="flex flex-col gap-2 w-auto py-2 px-4 border-border"
        align="start"
        sideOffset={4}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sorting.map((sort) => sort.id)}
            strategy={verticalListSortingStrategy}
          >
            {sorting.map((sort) => (
              <SortRow
                key={sort.id}
                selectedSort={sort}
                filtersList={cleanFiltersList}
                handleSortSelect={onSortUpdate}
                clearSorts={clearSorts}
              />
            ))}
          </SortableContext>
        </DndContext>
        <div className="flex flex-col">
          <FiltersCombobox
            triggerTitle="Add sort"
            searchPlaceholder="Sort By"
            emptyMessage="No sort found."
            triggerIcon={<PlusIcon className="size-3 text-primary" />}
            filtersList={cleanFiltersList}
            onFilterSelect={onSortSelect}
            className="min-h-[28px] py-1 text-xs border-none justify-start text-primary bg-muted hover:bg-muted/80"
            contentAlign="start"
          />
          <Button
            variant="ghost"
            className="gap-2.5 w-full justify-start text-xs text-muted-foreground bg-transparent hover:text-destructive hover:bg-transparent"
            onClick={clearAllSorts}
          >
            <Trash2 className="size-3.5" />
            Delete Sort
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

const MemoizedSortCombobox = memo(SortCombobox, (prevProps, nextProps) => {
  return (
    prevProps.sorting === nextProps.sorting &&
    prevProps.filtersList === nextProps.filtersList &&
    prevProps.newlyAddedSort === nextProps.newlyAddedSort &&
    prevProps.className === nextProps.className
  )
})

MemoizedSortCombobox.displayName = 'SortCombobox'

export { MemoizedSortCombobox as SortCombobox }
